import { useMutation, useQueryClient } from '@tanstack/react-query';

import { activateMtr } from '#/api/imsMgmt/settings/mtr/activate';
import { deactivateMtr } from '#/api/imsMgmt/settings/mtr/deactivate';
import {
  getMtrApiErrorMessage,
  isMtrVersionConflict,
} from '#/modules/imsMgmt/settings/mtr/helper/apiError';
import {
  mtrDetailQueryKey,
  mtrListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

/** 與 SideMsg／Detail CommandBar／編輯頁文案一致。 */
export const MTR_ACTIVATE_CONFIRM = {
  title: '啟用物料',
  message: '確定啟用此物料？啟用後可被選料／工單等流程使用。',
  confirmText: '啟用',
};

export const MTR_DEACTIVATE_CONFIRM = {
  title: '停用物料',
  message: '確定停用此物料？停用後仍可讀取，但不可被選料／工單等流程使用。',
  confirmText: '停用',
};

/**
 * 物料啟用／停用 mutation（confirm 由呼叫端處理）。
 *
 * @param {object} options
 * @param {string} options.id
 * @param {() => string|null|undefined} options.getUpdatedAt
 * @param {() => void|Promise<void>} [options.onAfterSuccess]
 * @param {() => void|Promise<void>} [options.onVersionConflict]
 */
function useMtrStatusMutation({
  id,
  getUpdatedAt,
  onAfterSuccess,
  onVersionConflict,
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ action }) => {
      const payload = { updatedAt: getUpdatedAt?.() };
      if (action === 'activate') {
        return activateMtr(id, payload);
      }
      if (action === 'deactivate') {
        return deactivateMtr(id, payload);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      const fallback = variables?.action === 'deactivate' ? '物料已停用' : '物料已啟用';
      toast.success(result?.message || fallback);
      await queryClient.invalidateQueries({ queryKey: mtrListQueryKeyPrefix() });
      if (id) {
        await queryClient.invalidateQueries({ queryKey: mtrDetailQueryKey(id) });
      }
      await onAfterSuccess?.(result);
    },
    onError: (error) => {
      toast.error(getMtrApiErrorMessage(error, '操作失敗'));
      if (isMtrVersionConflict(error)) {
        onVersionConflict?.(error);
      }
    },
  });
}

export default useMtrStatusMutation;
